
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Edit, Trash, Check, X } from 'lucide-react';
import { Category } from './types';
import { Card } from '@/components/ui/card';

interface CategoryTableProps {
  categories: Category[];
  onEdit: (category: Category) => void;
  onDelete: (id: string) => void;
  isMobile?: boolean;
}

export const CategoryTable: React.FC<CategoryTableProps> = ({
  categories,
  onEdit,
  onDelete,
  isMobile = false
}) => {
  if (categories.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        No categories found. Create your first category!
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="space-y-4">
        {categories.map((category) => (
          <Card key={category.id} className="p-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-medium">{category.name}</h3>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(category)}>
                  <Edit size={16} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => onDelete(category.id)}>
                  <Trash size={16} />
                </Button>
              </div>
            </div>
            <div className="text-sm text-gray-500">Slug: {category.slug}</div>
            <div className="text-sm text-gray-500 mt-2">
              {category.description && category.description.length > 100 
                ? `${category.description.substring(0, 100)}...` 
                : category.description || 'No description'}
            </div>
            <div className="mt-2">
              {category.active ? (
                <span className="flex items-center text-sm">
                  <Check size={14} className="mr-1 text-green-500" />
                  Active
                </span>
              ) : (
                <span className="flex items-center text-sm">
                  <X size={14} className="mr-1 text-red-500" />
                  Inactive
                </span>
              )}
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Slug</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {categories.map((category) => (
          <TableRow key={category.id}>
            <TableCell className="font-medium">{category.name}</TableCell>
            <TableCell>{category.slug}</TableCell>
            <TableCell>
              {category.description && category.description.length > 50 
                ? `${category.description.substring(0, 50)}...` 
                : category.description || '-'}
            </TableCell>
            <TableCell>
              {category.active ? (
                <span className="flex items-center">
                  <Check size={16} className="mr-1 text-green-500" />
                  Active
                </span>
              ) : (
                <span className="flex items-center">
                  <X size={16} className="mr-1 text-red-500" />
                  Inactive
                </span>
              )}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end space-x-2">
                <Button variant="outline" size="sm" onClick={() => onEdit(category)}>
                  <Edit size={16} />
                </Button>
                <Button variant="outline" size="sm" onClick={() => onDelete(category.id)}>
                  <Trash size={16} />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
